import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, ArrowRight, Pizza, Beef } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../lib/utils';
import StampCard from '../components/StampCard';

const LoyaltyPage = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      apiRequest('/loyalty/status')
        .then(data => setLoyaltyStatus(data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen py-16">
        <div className="max-w-md mx-auto px-4 text-center">
          <Gift className="h-16 w-16 text-[#FFC107] mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white font-['Barlow_Condensed'] uppercase mb-4">{t('loyalty.title')}</h1>
          <p className="text-zinc-400 mb-8">{t('loyalty.loginRequired')}</p>
          <Link to="/login">
            <Button className="bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold">{t('auth.login')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-zinc-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="loyalty-page">
      <div className="max-w-lg mx-auto px-4">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed'] mb-2">
            {t('loyalty.title')}
          </h1>
          <p className="text-zinc-400">{t('loyalty.subtitle')}</p>
        </div>

        {/* Stamp Card */}
        {loyaltyStatus && (
          <StampCard
            stamps={loyaltyStatus.stamps}
            stampsRequired={loyaltyStatus.stamps_required}
            rewardAvailable={loyaltyStatus.reward_available}
          />
        )}

        {/* How it works */}
        <Card className="bg-[#141417] border-[#27272A] mt-8">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-white mb-4 font-['Barlow_Condensed'] uppercase">{t('loyalty.howItWorks')}</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FFC107]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-[#FFC107]">1</span>
                </div>
                <div>
                  <p className="text-white font-medium">{t('loyalty.step1Title')}</p>
                  <p className="text-sm text-zinc-400">{t('loyalty.step1Desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FFC107]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-[#FFC107]">2</span>
                </div>
                <div>
                  <p className="text-white font-medium">{t('loyalty.step2Title')}</p>
                  <p className="text-sm text-zinc-400">{t('loyalty.step2Desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FFC107]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-[#FFC107]">3</span>
                </div>
                <div>
                  <p className="text-white font-medium">{t('loyalty.step3Title')}</p>
                  <p className="text-sm text-zinc-400">{t('loyalty.step3Desc')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Available Rewards */}
        <Card className="bg-[#141417] border-[#27272A] mt-4">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-white mb-4 font-['Barlow_Condensed'] uppercase">{t('loyalty.availableRewards')}</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-[#1C1C22]/50 rounded-lg">
                <Pizza className="h-5 w-5 text-[#FFC107]" />
                <span className="text-white">Pizza Marinara</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-[#1C1C22]/50 rounded-lg">
                <Pizza className="h-5 w-5 text-[#FFC107]" />
                <span className="text-white">Pizza Margherita</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-[#1C1C22]/50 rounded-lg">
                <Beef className="h-5 w-5 text-[#FFC107]" />
                <span className="text-white">Classic Burger</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link to="/menu">
            <Button className="bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wide h-12 px-8" data-testid="loyalty-order-now-btn">
              {t('hero.orderNow')} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyPage;
